/**
 * This component contains code to edit a user access.
 * This component is accessible from user management page.
 */
import Button from "@mui/material/Button";
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { useUpdateUserRoles } from "services/users.service";
import useForm from "hooks/useForm";
import "./EditUser.scss";
import RolesDropdown from "components/RolesDropdown";
import { useQueryClient } from "@tanstack/react-query";
import DashedDivider from "common/ui/DashedDivider";
import { useAppDispatch, useAppSelector } from "context/hooks";
import { selectEditUser } from "context/modal.selector";
import { setEditUser } from "context/modal.context";
import toast from "react-hot-toast";
import LoadingButton from "common/ui/LoadingButton";

function EditUser() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const props = useAppSelector(selectEditUser);

  const userRoles = props.user?.roles?.map(
    ({ roleId, roleName }) => `${roleId}|${roleName}`
  );
  // form state
  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors }
  } = useForm({
    defaultValues: { userRoles }
  });

  const updateRoles = useUpdateUserRoles(onRoleUpdated);

  function handleEditUser(data) {
    const roleId = data.userRoles.map((role) => {
      const [roleId] = role.split("|");
      return roleId;
    });

    updateRoles.mutate({ userId: props.user?.userId, roleId });
  }

  function onRoleUpdated(_data, error) {
    queryClient.invalidateQueries({ queryKey: ["all-portal-users"] });

    let message = t("editUser.userUpdatedSuccessfully");
    if (!error) {
      toast.success(message);
    }

    clearErrors();
    dismiss();
  }

  function dismiss() {
    dispatch(
      setEditUser({
        open: false
      })
    );
  }

  function onModalDismiss() {
    clearErrors();
    dismiss();
  }
  return (
    <Dialog open={props.open} maxWidth="md" fullWidth>
      <DialogTitle
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        {t("editUser.header")}
        <Stack direction="row" spacing={2}>
          <IconButton onClick={onModalDismiss}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DashedDivider style={{ width: "95%" }} />
      <DialogContent
        classes={{
          root: "edit-user-modal__content"
        }}
      >
        <Grid container alignItems="center">
          <Grid item xs={5}>
            <Typography noWrap fontWeight="medium" color="primary.dark">
              {`${props.user?.firstName} ${props.user?.lastName}`}
            </Typography>
            <Typography noWrap fontWeight="light" color="primary.dark">
              {props.user?.email}
            </Typography>
          </Grid>
          <Grid item xs={5}>
            <RolesDropdown
              selectedRoles={userRoles}
              roles={props.roles}
              inputProps={{
                ...register("userRoles", {
                  required: t("editUser.roleRequired")
                })
              }}
              error={errors?.userRoles}
            />
          </Grid>
          <Grid item xs={2} container justifyContent="flex-end">
            <LoadingButton
              variant="outlined"
              onClick={handleSubmit(handleEditUser)}
              loading={updateRoles.isLoading}
            >
              {t("common.save")}
            </LoadingButton>
          </Grid>
        </Grid>
      </DialogContent>
      <DashedDivider style={{ width: "95%" }} />
      <DialogActions sx={{ justifyContent: "center", padding: 2 }}>
        <Button
          size="large"
          variant="outlined"
          color="gray"
          onClick={onModalDismiss}
        >
          {t("common.cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditUser;
